import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update by keeping the current gallery order and moving to the selected media.
   * The gallery is only replaced for combined-listing product changes.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const data = event.detail?.data ?? {};
    const source = data.html;
    const newMediaGallery = source?.querySelector?.('media-gallery');

    const selectedMediaId =
      data.selectedMediaId ||
      newMediaGallery?.dataset?.selectedVariantMediaId ||
      newMediaGallery?.getAttribute?.('data-selected-variant-media-id');

    if (selectedMediaId && this.#selectMediaById(selectedMediaId)) return;

    if (newMediaGallery) {
      const selectedMediaFromUpdatedGallery = this.#getSelectedMediaFromUpdatedGallery(newMediaGallery);
      const mediaId = selectedMediaFromUpdatedGallery ? this.#getMediaId(selectedMediaFromUpdatedGallery) : undefined;

      if (mediaId && this.#selectMediaById(mediaId)) return;
    }

    // If this is a different product from a combined listing, the old gallery may not contain that media.
    // Only in that case do we replace the gallery.
    if (data.newProduct && newMediaGallery) {
      this.replaceWith(newMediaGallery);
    }
  };

  /**
   * Finds the media item that the newly rendered gallery considers active/selected.
   *
   * @param {Element} mediaGallery - The newly rendered media gallery.
   * @returns {HTMLElement | undefined}
   */
  #getSelectedMediaFromUpdatedGallery(mediaGallery) {
    const selectedMediaId = mediaGallery.getAttribute('data-selected-variant-media-id');

    if (selectedMediaId) {
      return this.#getMediaItems(mediaGallery).find((media) => this.#mediaMatchesId(media, selectedMediaId));
    }

    const mediaItems = this.#getMediaItems(mediaGallery);
    if (!mediaItems.length) return undefined;

    const selectedMediaSelectors = [
      '[aria-current="true"]',
      '[aria-current="page"]',
      '[aria-selected="true"]',
      '[data-selected="true"]',
      '[data-active="true"]',
      '[active]',
      '.is-active',
      '.active',
      '.slideshow__slide--active',
      '.slideshow-slide--active',
      '.swiper-slide-active',
    ];

    for (const selector of selectedMediaSelectors) {
      const selectedElement = mediaGallery.querySelector(selector);
      if (!(selectedElement instanceof HTMLElement)) continue;

      const selectedMedia = mediaItems.find(
        (media) => media === selectedElement || media.contains(selectedElement) || selectedElement.contains(media)
      );

      if (selectedMedia) return selectedMedia;
    }

    return undefined;
  }

  /**
   * Selects and scrolls to a media item while keeping the existing DOM order unchanged.
   *
   * @param {string | number} mediaId - The selected media id.
   * @returns {boolean} Whether matching media was found.
   */
  #selectMediaById(mediaId) {
    const mediaItems = this.#getMediaItems();
    const index = mediaItems.findIndex((media) => this.#mediaMatchesId(media, mediaId));

    if (index === -1) return false;

    const selectedMedia = this.#getBestScrollTarget(mediaId) || mediaItems[index];

    this.slideshow?.select(index, undefined, { animate: true });
    this.preloadImage(index);

    // On mobile the product gallery is a carousel. Keep the native carousel behavior only,
    // and do not force a page scroll that could fight the swipe/slide position.
    if (!this.#isMobileViewport()) {
      window.requestAnimationFrame(() => {
        this.#scrollMediaToViewportCenter(selectedMedia);
      });
    }

    return true;
  }

  /**
   * Gets the best visible item to scroll to for the selected media id.
   *
   * @param {string | number} mediaId - The selected media id.
   * @returns {HTMLElement | undefined}
   */
  #getBestScrollTarget(mediaId) {
    const matches = this.#getAllMediaItems().filter((media) => this.#mediaMatchesId(media, mediaId));

    return matches.find((media) => this.#isVisible(media)) || matches[0];
  }

  /**
   * Checks whether the current viewport is using the mobile carousel layout.
   *
   * @returns {boolean}
   */
  #isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Scrolls the selected media so its vertical center is aligned with the viewport center.
   * This is desktop-only; mobile keeps the native carousel behavior.
   *
   * @param {HTMLElement} media - The media element to center in the viewport.
   */
  #scrollMediaToViewportCenter(media) {
    if (!media || !this.#isVisible(media)) return;

    const rect = media.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const currentScrollY = window.scrollY || window.pageYOffset;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );

    const targetScrollY = currentScrollY + rect.top - (viewportHeight - rect.height) / 2;
    const maxScrollY = Math.max(0, documentHeight - viewportHeight);
    const centeredScrollY = Math.min(Math.max(targetScrollY, 0), maxScrollY);

    window.scrollTo({
      top: centeredScrollY,
      behavior: 'smooth',
    });
  }

  /**
   * Gets the main gallery media items in product-gallery order.
   * Slideshow slides are preferred because they exist for carousel and mobile gallery modes.
   *
   * @param {Element} [root=this] - The root element to search.
   * @returns {HTMLElement[]}
   */
  #getMediaItems(root = this) {
    const slideshowItems = this.#uniqueMediaItems(
      Array.from(root.querySelectorAll('slideshow-slide[data-media-id]')).filter(
        (media) => media instanceof HTMLElement && !media.closest('zoom-dialog')
      )
    );

    if (slideshowItems.length) return slideshowItems;

    const gridItems = this.#uniqueMediaItems(
      Array.from(root.querySelectorAll('.media-gallery__grid [data-media-id]')).filter(
        (media) => media instanceof HTMLElement && !media.closest('zoom-dialog')
      )
    );

    if (gridItems.length) return gridItems;

    if (root === this && this.media?.length) {
      return this.#uniqueMediaItems(
        Array.from(this.media).filter((media) => media instanceof HTMLElement && !media.closest('zoom-dialog'))
      );
    }

    return this.#uniqueMediaItems(
      Array.from(root.querySelectorAll('[data-media-id], [ref="media[]"], [id*="Media"], [id*="media"]')).filter(
        (media) => media instanceof HTMLElement && !media.closest('zoom-dialog')
      )
    );
  }

  /**
   * Gets all main-gallery media items, including grid and slideshow duplicates, for finding a visible scroll target.
   *
   * @returns {HTMLElement[]}
   */
  #getAllMediaItems() {
    return Array.from(
      this.querySelectorAll('slideshow-slide[data-media-id], .media-gallery__grid [data-media-id], [ref="media[]"][data-media-id]')
    ).filter((media) => media instanceof HTMLElement && !media.closest('zoom-dialog'));
  }

  /**
   * Deduplicates media items by media id while preserving order.
   *
   * @param {HTMLElement[]} mediaItems - The media items.
   * @returns {HTMLElement[]}
   */
  #uniqueMediaItems(mediaItems) {
    const seen = new Set();
    const uniqueItems = [];

    for (const media of mediaItems) {
      const mediaId = this.#getMediaId(media) || media.id;
      if (!mediaId || seen.has(mediaId)) continue;

      seen.add(mediaId);
      uniqueItems.push(media);
    }

    return uniqueItems;
  }

  /**
   * Checks whether a media element is currently visible.
   *
   * @param {HTMLElement} media - The media element.
   * @returns {boolean}
   */
  #isVisible(media) {
    return !!(media.offsetWidth || media.offsetHeight || media.getClientRects().length);
  }

  /**
   * Checks whether a media element matches a Shopify media id.
   *
   * @param {HTMLElement} media - The media element.
   * @param {string | number} mediaId - The id to match.
   * @returns {boolean}
   */
  #mediaMatchesId(media, mediaId) {
    const mediaElementId = this.#getMediaId(media);
    return this.#idsMatch(mediaElementId, mediaId);
  }

  /**
   * Extracts a normalized media id from common Shopify media attributes.
   *
   * @param {HTMLElement} media - The media element.
   * @returns {string | undefined}
   */
  #getMediaId(media) {
    const attributes = ['data-media-id', 'data-target', 'aria-controls', 'id'];

    for (const attribute of attributes) {
      const normalizedId = this.#normalizeMediaId(media.getAttribute(attribute));
      if (normalizedId) return normalizedId;
    }

    const childWithMediaId = media.querySelector('[data-media-id], [data-target], [aria-controls], [id*="Media"], [id*="media"]');
    if (childWithMediaId instanceof HTMLElement) {
      for (const attribute of attributes) {
        const normalizedId = this.#normalizeMediaId(childWithMediaId.getAttribute(attribute));
        if (normalizedId) return normalizedId;
      }
    }

    return undefined;
  }

  /**
   * Compares ids after normalizing Shopify section/media id formats.
   *
   * @param {string | number | undefined} firstId - The first id.
   * @param {string | number | undefined} secondId - The second id.
   * @returns {boolean}
   */
  #idsMatch(firstId, secondId) {
    const first = this.#normalizeMediaId(firstId);
    const second = this.#normalizeMediaId(secondId);

    return !!first && !!second && first === second;
  }

  /**
   * Normalizes values like "#Slide-template--123__main-456" to "456".
   *
   * @param {string | number | null | undefined} value - The value to normalize.
   * @returns {string | undefined}
   */
  #normalizeMediaId(value) {
    if (value === null || value === undefined) return undefined;

    const stringValue = String(value).trim().replace(/^#/, '');
    if (!stringValue) return undefined;

    const numericParts = stringValue.match(/\d+/g);
    if (numericParts?.length) return numericParts[numericParts.length - 1];

    return stringValue;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}